import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  type HotelAppLoadedRawEvent,
  type HotelAvailabilityPageLoadedRawEvent,
  type HotelCoverViewed,
  type HotelEntry,
  type HotelListLoaded,
  type HotelsRawEvent,
  type HotelShopPageLoadedRawEvent,
  type HotelHTSEvent,
  type HotelUpdatedListPage,
  type HotelUpdatedListPageRawEvent,
  HotelsRawEventName,
  HotelCommonTrackingEventsName,
  HotelsAvailabilitySelectors,
  HOTEL_HTS_EVENT_TRANSFORMER_HOOKS,
  commonProperties,
  useHotelAvailabilityPageLoadedTransformer,
  useHotelShopPageLoadedTransformer,
  useHotelUpdatedListPageTransformer,
} from "@b2bportal/core-hotels";
import type { UseInstantTransformEvent } from "@b2bportal/core-tracking";
import type { EventTransformerHooks } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import type { LodgingTravelWalletOffer } from "@b2bportal/lodging-api";
import { useTrackFunnelEntry } from "./hooks";
import { HOTEL_3X_EARN_OFFER_NAME, OfferType } from "./offers/constants";
import { getOfferFunnelEntrySource } from "./offers/offerFunnelEntrySource";
import { usePackageFunnelAttribution } from "./upSell";
import { useBonusEarnResource } from "@/modules/wallet/BonusEarnBanner/useBonusEarnResource";
import { isHtsMediaOffer } from "@/modules/wallet/WalletOfferBanner/WalletOfferBanner";

/**
 * RBC-specific transformer for hotel_entry event.
 * Fires on every funnel transition (not just once per session).
 */
export const useRbcHotelAppLoadedTransformer: UseInstantTransformEvent<
  HotelAppLoadedRawEvent,
  HotelEntry
> = () => {
  const shouldTrack = useTrackFunnelEntry(Funnel.Lodging);
  // 3X earn applies whenever the user is eligible, regardless of funnel entry.
  const is3xEligible = useBonusEarnResource() != null;

  return useCallback(
    (event) => {
      if (!shouldTrack) {
        return undefined;
      }

      const name: HotelCommonTrackingEventsName.hotel_entry =
        HotelCommonTrackingEventsName.hotel_entry;

      const entrySource = getOfferFunnelEntrySource();

      // offer_name and offer_type carry only 3X here. Hotel deals are driven by
      // strikethrough pricing, which isn't loaded when hotel_entry fires (it runs
      // on app load, before room rates resolve), so a deal can't be confirmed.
      // entry_source still records how the user entered (offers page / banner).
      return {
        name,
        properties: {
          entry: event.properties.entry,
          ...commonProperties,
          ...(entrySource ? { entry_source: entrySource } : {}),
          ...(is3xEligible
            ? {
                offer_name: HOTEL_3X_EARN_OFFER_NAME,
                offer_type: OfferType.rbc_funded_points,
              }
            : {}),
        },
      };
    },
    [shouldTrack, is3xEligible]
  );
};

/** Extracts offer tracking properties from the availability bestOfferOverall. */
export const getOfferAttributionFromAvailability = (
  bestOffer: LodgingTravelWalletOffer | undefined
) => {
  if (bestOffer == null) return {};
  const props = bestOffer.trackingPropertiesV2?.properties;
  const offerId =
    typeof props?.offer_id === "string" ? props.offer_id : bestOffer.id;
  const offerName =
    typeof props?.offer_name === "string" ? props.offer_name : bestOffer.title;
  const offerType =
    typeof props?.offer_type === "string" ? props.offer_type : undefined;
  return {
    offer_id: offerId,
    offer_name: offerName,
    offer_type: offerType,
    is_claimable: isHtsMediaOffer(bestOffer),
  };
};

/**
 * The packages funnel reuses the core hotel list and cover pages, so these are
 * the events it emits. The attribution is empty outside the packages funnel.
 */
export const useRbcHotelListLoadedTransformer: UseInstantTransformEvent<
  HotelAvailabilityPageLoadedRawEvent,
  HotelListLoaded
> = (name) => {
  const transform = useHotelAvailabilityPageLoadedTransformer(name);
  const attribution = usePackageFunnelAttribution();
  const bestOffer = useSelector(
    HotelsAvailabilitySelectors.getBestOfferOverall
  );
  const offerAttribution = getOfferAttributionFromAvailability(bestOffer);

  return useCallback(
    (event) => {
      const result = transform(event);
      if (result == null) return result;

      return {
        ...result,
        properties: {
          ...result.properties,
          ...attribution,
          ...offerAttribution,
        },
      };
    },
    [transform, attribution, offerAttribution]
  );
};

export const useRbcHotelUpdatedListPageTransformer: UseInstantTransformEvent<
  HotelUpdatedListPageRawEvent,
  HotelUpdatedListPage
> = (name) => {
  const transform = useHotelUpdatedListPageTransformer(name);
  const attribution = usePackageFunnelAttribution();

  return useCallback(
    (event) => {
      const result = transform(event);
      if (result == null) return result;

      return {
        ...result,
        properties: { ...result.properties, ...attribution },
      };
    },
    [transform, attribution]
  );
};

export const useRbcHotelCoverViewedTransformer: UseInstantTransformEvent<
  HotelShopPageLoadedRawEvent,
  HotelCoverViewed
> = (name) => {
  const transform = useHotelShopPageLoadedTransformer(name);
  const attribution = usePackageFunnelAttribution();

  return useCallback(
    (event) => {
      const result = transform(event);
      if (result == null) return result;

      return {
        ...result,
        properties: { ...result.properties, ...attribution },
      };
    },
    [transform, attribution]
  );
};

/**
 * RBC-specific hotel event transformer hooks (overrides only)
 */
export const RBC_HOTEL_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  HotelsRawEvent,
  HotelHTSEvent
> = {
  [HotelsRawEventName.HotelAppLoaded]: [useRbcHotelAppLoadedTransformer],
  [HotelsRawEventName.HotelAvailabilityPageLoaded]: [
    useRbcHotelListLoadedTransformer,
  ],
  [HotelsRawEventName.HotelUpdatedListPage]: [
    useRbcHotelUpdatedListPageTransformer,
  ],
  [HotelsRawEventName.HotelShopPageLoaded]: [useRbcHotelCoverViewedTransformer],
};

/**
 * All RBC hotel transformers - core transformers with RBC overrides
 * RBC transformers REPLACE core transformers for the same event (not merge)
 */
export const ALL_RBC_HOTEL_TRANSFORMERS: EventTransformerHooks<
  HotelsRawEvent,
  HotelHTSEvent
> = {
  // Start with all core transformers
  ...HOTEL_HTS_EVENT_TRANSFORMER_HOOKS,
  // Override with RBC-specific transformers (replaces, not merges)
  ...RBC_HOTEL_HTS_EVENT_TRANSFORMER_HOOKS,
};
